import { reactive } from 'vue';
import { tryNew } from '@/helpers/dto2resource';
import DividaDto from '@/models/dto/DividaDto';
import DividaPropostaDto from '@/models/dto/DividaPropostaDto';
import PropostaNovoDto from '@/models/dto/PropostaNovoDto';
import WlSincronizacaoDto from '@/models/dto/WlSincronizacaoDto';
import { StatusDivida, StatusProposta, TipoNegociacaoOferta } from '@/models/enums';
import { DividaResource, PropostaNovoResource } from '@/models/resources';
import DetalhesPropostaResource from '@/models/resources/DetalhesPropostaResource';
import usePropostasStore from '@/stores/propostas';

/* eslint-disable class-methods-use-this */
class ConvivenciaWlService {
  public isWhiteLabel(api: string) {
    return !api.startsWith('mc-');
  }

  public async filtrarNegociaveisEAtualizarStorePropostas(
    api: string,
    sincWlDto: WlSincronizacaoDto,
  ): Promise<DividaResource[]> {
    const statusDividaDisponiveis = new Set([
      StatusDivida.AguardandoNegociacao,
    ]);
    const dividasComPropostas = sincWlDto.dividas
      .filter((d) => !statusDividaDisponiveis.has(d.status) && d.ultimaProposta);

    const propostasConvertidas = dividasComPropostas
      .map((d) => this.fromDividaToPropostaNovoResource(d))
      .filter((p): p is PropostaNovoResource => p !== undefined);

    const propostas: PropostaNovoResource[] = [];

    propostas.push(
      ...sincWlDto.propostasQuitadas.map((p) => {
        const resource = new PropostaNovoResource(p);
        resource.status = StatusProposta.Quitada;
        return resource;
      }),
      ...propostasConvertidas,
    );

    const store = usePropostasStore();
    store.atualizarTodasPropostas(api, propostas);

    return sincWlDto.dividas
      .filter((d) => statusDividaDisponiveis.has(d.status))
      .map((d) => new DividaResource(d));
  }

  public fromDividaToPropostaNovoResource(divida: DividaDto): PropostaNovoResource | undefined {
    if (!divida.ultimaProposta) {
      return undefined;
    }
    const dto = this.toPropostaNovoDto(divida);
    if (!dto) {
      throw new Error('Falha ao converter última proposta da dívida');
    }
    const retorno = new PropostaNovoResource(dto);
    retorno.divida.fluxoNegociacao = divida.fluxoNegociacao;
    retorno.detalhe = this.criarDetalhe(divida.ultimaProposta.detalhe);
    return retorno;
  }

  public fromPropostaToPropostaNovoResource(proposta: PropostaNovoDto)
    : PropostaNovoResource {
    const propostaGerada = new PropostaNovoResource(proposta);
    if (proposta.parcelas.length > 0 && proposta.parcelas.every((p) => p.dataPagamento)) {
      propostaGerada.status = StatusProposta.Quitada;
    }
    propostaGerada.detalhe = this.criarDetalhe(proposta.detalhe);
    return propostaGerada;
  }

  private toPropostaNovoDto(divida: DividaDto)
    : PropostaNovoDto | undefined {
    const dividaProposta = this.toDividaPropostaDto(divida);
    const p = divida.ultimaProposta;
    return !p ? undefined : {
      id: p.id,
      status: divida.status === StatusDivida.Concluido ? StatusProposta.Quitada : p.status,
      dataProposta: `${p.dataProposta}`,
      divida: dividaProposta,
      parcelas: divida.boletos ?? [],
      valorEntrada: p.valorEntrada,
      parcelasGerenciadas: p.parcelasGerenciadas,
      possuiTermoAcordo: p.possuiTermoAcordo,
      urlTermoAcordo: p.urlTermoAcordo,
      valorTotal: p.valorTotal,
      quantidadeParcelas: p.quantidadeParcelas,
      valorParcela: p.valorParcela,
      dataPrimeiroBoleto: new Date(p.dataPrimeiroBoleto),
      possuiEntrada: p.possuiEntrada,
      meioPagamentoEscolhido: p.meioPagamentoEscolhido,
      meiosPagamentoPrimeiraParcela: p.meiosPagamentoPrimeiraParcela,
      meiosPagamentoDemaisParcelas: p.meiosPagamentoDemaisParcelas,
      negociacaoParcial:
        p.oferta?.informacoesAdicionais?.tipoNegociacao === TipoNegociacaoOferta.QuitacaoParcial,
      valorDesconto: this.calcularValorDesconto(divida),
      percentualDesconto: this.calcularPercentualDesconto(divida),
      oferta: p.oferta,
      permiteCancelamento: false,
      detalhe: this.criarDetalhe(p.detalhe),
    };
  }

  private calcularValorDesconto(d: DividaDto): number {
    if (!d.ultimaProposta || !d.valorTotal) return 0;

    return d.ultimaProposta.oferta?.detalhe?.valores?.valorDesconto
      ?? d.valorTotal - d.ultimaProposta.valorTotal;
  }

  private calcularPercentualDesconto(d: DividaDto): number {
    if (!d.valorTotal) return 0;

    return d.ultimaProposta?.oferta?.detalhe?.valores?.percentualDesconto
      ?? Math.trunc((this.calcularValorDesconto(d) * 100) / d.valorTotal);
  }

  private toDividaPropostaDto(divida: DividaDto): DividaPropostaDto {
    return {
      id: divida.id,
      codigoContrato: divida.codigoContrato,
      codigoCredor: divida.codigoCredor,
      descricao: divida.descricao,
      dataOrigem: divida.dataOrigem,
      dataChegada: divida.dataChegada,
      valorPrincipal: divida.valorPrincipal,
      valorTotal: divida.valorTotal,
      credor: divida.credor,
      credorOrigem: divida.credorOrigem,
      produto: divida.produto,
      api: divida.api,
      detalhe: divida.detalhe,
      parametros: divida.parametros || undefined,
      composicao: divida.composicao || [],
    };
  }

  private criarDetalhe(
    detalhe: PropostaNovoDto['detalhe'],
  ): DetalhesPropostaResource | undefined {
    if (!detalhe) return undefined;
    if (typeof detalhe === 'string') {
      return DetalhesPropostaResource.fromWhiteLabel(detalhe);
    }
    if (typeof detalhe === 'object' && detalhe !== null) {
      if ('descricao' in detalhe && typeof (detalhe as { descricao?: string }).descricao === 'string') {
        return DetalhesPropostaResource.fromWhiteLabel(
          (detalhe as { descricao?: string }).descricao ?? '',
        );
      }
      return tryNew(DetalhesPropostaResource, detalhe);
    }
    return undefined;
  }
}

const convivenciaWlService = new ConvivenciaWlService();
export default reactive(convivenciaWlService);
